import { isEmpty, sortBy } from 'lodash';
import { ERROR_TYPE } from '../data-types/error/errorEnums';
import { DEFAULT_ERROR_MESSAGE } from '../data-types/initialisation/initialisationConstants';
import { CommandResponseError } from '../data-types/initialisation/initialisationInterfaces';
import { limitTimeFrames } from '../data-types/profile/profile-sections/staying-in-control/stayingInControlConstants';
import { LimitType } from '../data-types/profile/profile-sections/staying-in-control/stayingInControlEnums';
import { VALIDATION_TYPE } from '../data-types/validation/validationEnums';
import { ZIMPLER_SIGNUP_PAYMENT_METHOD } from '../data-types/wallet/walletConstants';
import { CardType, MOBILE_PAYMENT, PAYMENT_METHODS_TRANSACTION_TYPE_ID, SIGNUP_OPTION } from '../data-types/wallet/walletEnums';
import {
  AcknowledgeDepositInformationResponse,
  CancelWithdrawalResponse,
  CurrencySettingsResponse,
  DepositRequestResponse,
  NetDepositsResponse,
  PaymentData,
  PaymentMethodsResponse,
  PaymentMethodWithNoAccounts,
  RequestWithdrawalPayload,
  SanitizedPaymentMethod,
  TransactionHistoryItem,
  Variants,
  WalletCurrentResponse,
  WalletHistoryResponse,
  WalletProfitAndLossBalanceResponse,
  WalletSettings,
  Withdrawal,
  WithdrawalItem,
  WithdrawalRequestResponse,
  WithdrawalResponse,
} from '../data-types/wallet/walletInterfaces';
import { fingerprint } from '../initialisation/initialisation';
import { getCurrencySymbol } from '../international/internationalCurrencies';
import { userAcknowledgeDepositInformation } from '../socket-types/userScoketTypes';
import {
  paymentsDepositsDeposit,
  paymentsMethodsList,
  paymentsWithdrawalsCancel,
  paymentsWithdrawalsList,
  paymentsWithdrawalsWithdraw,
  walletCurrency,
  walletCurrent,
  walletHistoryCommand,
  walletNetDeposits,
  walletProfitLossBalance,
} from '../socket-types/walletSocketTypes';
import { setUserDetails } from '../state/actions/userDataActions';
import {
  setDepositCardType,
  setMobilePaymentInitDialog,
  setUserBalance,
  setUserBonusWallet,
  setUserTotalBalance,
} from '../state/actions/walletActions';
import { store } from '../state/store';
import { tryEnqueueDepositNotificationAndResetDepositInfo } from '../utils/notifications';
import { convertToPeriod } from '../utils/periodConverter';
import { checkPostZimplerSignup } from '../utils/signup';
import { isOfType } from '../validation/typeValidation';
import { sendCommand } from './commandsHandler';
import { setLimit } from './userCommands';

export const getBalance = async () => {
  const res = await sendCommand<WalletCurrentResponse>(walletCurrent, {});
  if (res.data) {
    const { available, bonusWalletData } = res.data;

    const balanceAvailable = available || 0;
    const bonusAmount = bonusWalletData ? bonusWalletData.balance : 0;
    const totalBalanceAvailable = balanceAvailable + bonusAmount;

    if (available !== undefined && isOfType(available, VALIDATION_TYPE.NUMBER)) {
      setUserBalance(Number(available));
      setUserTotalBalance(Number(totalBalanceAvailable));
      checkPostZimplerSignup(available);
    }

    if (!!bonusWalletData && !isEmpty(bonusWalletData)) {
      setUserBonusWallet(bonusWalletData);
    }

    return;
  }

  // handle errors from the BE
  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const getProfitAndLossBalance = async (start: string, end: string): Promise<number> => {
  const res = await sendCommand<WalletProfitAndLossBalanceResponse>(walletProfitLossBalance, {
    dateRange: {
      start,
      end,
    },
  });

  if (res.data && isOfType(res.data.profitLoss, VALIDATION_TYPE.NUMBER)) {
    return Number(res.data.profitLoss);
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const setDepositLimit = async (timeFrameId: string | undefined, amount: number | null): Promise<CommandResponseError | void> => {
  let period: number | null = null;
  if (timeFrameId) {
    period = convertToPeriod(timeFrameId, limitTimeFrames);
  }
  await setLimit(amount, LimitType.DEPOSIT, period);
};

export const acknowledgeDepositInformation = async (tag?: SIGNUP_OPTION): Promise<void> => {
  const res = await sendCommand<AcknowledgeDepositInformationResponse>(userAcknowledgeDepositInformation, { tag, uuid: fingerprint });

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data) {
    return;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const netDeposits = async (): Promise<{ deposits: number; withdrawals: number; overall: number }> => {
  const res = await sendCommand<NetDepositsResponse>(walletNetDeposits, {});

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data) {
    const net = {
      deposits: res.data.deposits,
      withdrawals: res.data.withdrawals,
      overall: res.data.net,
    };

    setUserDetails({ net });
    return net;
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

export const walletHistory = async (data: {
  pageIndex?: number;
  createdAt: { gte: string; lte: string };
}): Promise<TransactionHistoryItem[]> => {
  const res = await sendCommand<WalletHistoryResponse>(walletHistoryCommand, data);

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  if (res.data) {
    return res.data.transactions.map(a => ({
      id: a.id,
      name: a.name,
      type: a.type,
      referenceId: a.referenceId,
      createdAt: a.createdAt,
      amount: a.amount,
      balance: a.balance,
      amountString: a.amountString,
      bonusAmountString: a.bonusAmountString,
      bonusBalance: a.bonusBalance,
      data: {
        casino: a.data.casino || undefined,
        ticket: a.data.ticket || undefined,
      },
    }));
  }

  throw new Error(DEFAULT_ERROR_MESSAGE);
};

const getReadableCardLabel = (label: string, short?: boolean) => {
  // there are some providers that have the label value equal
  // to their email and we do not want to show that.
  if (label === ' ') {
    return '';
  }
  if (!isNaN(Number(label))) {
    if (short) {
      return `**** ${label}`;
    }
    return `**** **** **** ${label}`;
  }
  return label;
};

export const getWalletSettings = async (currencyISO: string): Promise<WalletSettings> => {
  const res = await sendCommand<CurrencySettingsResponse>(walletCurrency, { isoAlphabeticCode: currencyISO });

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  return res.data;
};

export const getPaymentMethods = async (
  transactionType: PAYMENT_METHODS_TRANSACTION_TYPE_ID,
  includeInactive?: boolean,
): Promise<{
  cards: SanitizedPaymentMethod[];
  canAddNew: boolean;
  paymentMethodsWithNoAccounts: PaymentMethodWithNoAccounts[];
}> => {
  const res = await sendCommand<PaymentMethodsResponse>(paymentsMethodsList, { transactionType, includeInactive });

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  const orderedPaymentMethodsWithNoAccounts = sortBy(res.data.methodsWithNoAccounts, 'orderNo');

  return {
    paymentMethodsWithNoAccounts: orderedPaymentMethodsWithNoAccounts,
    canAddNew: res.data.canAddNew,
    cards: res.data.methodsWithAccounts
      .filter(pm => pm.enabled)
      .map(pm => {
        return {
          id: pm.id || pm.reference,
          logo:
            (pm.variant &&
              res.data.variants &&
              res.data.variants[pm.variant] &&
              (res.data.variants[pm.variant].svg || res.data.variants[pm.variant].png)) ||
            '',
          color: (pm.variant && res.data.variants && res.data.variants[pm.variant] && res.data.variants[pm.variant].color) || '',
          name: pm.name,
          source: pm.source,
          reference: pm.reference,
          sourceReferenceId: pm.sourceReferenceId,
          requiredAccountId: pm.requiredAccountId,
          isActive: pm.isActive,
          closeableLoop: pm.closeableLoop,
          netDeposit: pm.netDeposit,
          maxWithdrawal: pm.maxWithdrawal,
          identifier: getReadableCardLabel(pm.label || (pm.reference && String(pm.reference)) || ''),
          identifierShort: getReadableCardLabel(pm.label || (pm.reference && String(pm.reference)) || '', true),
          expiry:
            (pm.cardDetails &&
              pm.cardDetails.expiryMonth &&
              pm.cardDetails.expiryYear &&
              `${pm.cardDetails.expiryMonth}/${pm.cardDetails.expiryYear}`) ||
            '',
        };
      }),
  };
};

export const executeDeposit = async (
  amount: number,
  paymentMethod: SanitizedPaymentMethod | PaymentMethodWithNoAccounts | undefined,
  paymentProvider: number | undefined,
  locale: string | undefined,
  tag: SIGNUP_OPTION | undefined,
): Promise<boolean | string | PaymentData> => {
  const res = await sendCommand<DepositRequestResponse>(paymentsDepositsDeposit, {
    amount,
    paymentProvider,
    locale,
    tag,
    method: paymentMethod,
  });

  if (!res.error && res.data && res.data.title && res.data.title === MOBILE_PAYMENT.DEPOSIT_REQUEST_SENT) {
    const {
      userData: { user, userLocation },
    } = store.getState();

    setMobilePaymentInitDialog({
      amount,
      open: true,
      phoneNumber: user && user.phoneNumber ? user.phoneNumber : '',
      currency: getCurrencySymbol(user, userLocation),
    });
    return res.data.title;
  }

  if (!res.error && res.data) {
    return res.data;
  }

  if (res.error && res.error.data && res.error.data.code && res.error.data.code === ERROR_TYPE.PAYMENT_DECLINED) {
    const {
      wallet: { depositInfo },
    } = store.getState();
    setDepositCardType(CardType.FAILED);
    tryEnqueueDepositNotificationAndResetDepositInfo(depositInfo.depositConfirmationType, '', '');
    return false;
  }

  if (res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  return true;
};

export const addPaymentMethod = async (
  amount: number,
  paymentProvider: number,
  locale: string,
  tag: SIGNUP_OPTION | undefined,
): Promise<PaymentData> => {
  let payload;

  if (tag === SIGNUP_OPTION.ZIMPLER_PNP) {
    payload = { amount, paymentProvider, locale, tag, method: ZIMPLER_SIGNUP_PAYMENT_METHOD };
  } else {
    payload = { amount, paymentProvider, locale, tag };
  }

  const res = await sendCommand<DepositRequestResponse>(paymentsDepositsDeposit, payload);

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  return res.data;
};

const sanitizeWithdrawals = (items: WithdrawalItem[], variants: Variants): Withdrawal[] =>
  items.map(item => ({
    id: item.id,
    logoSvg: item.method.iconSvgUrl || (item.method.variant && variants[item.method.variant] && variants[item.method.variant].svg) || '',
    logoPng: item.method.iconPngUrl || (item.method.variant && variants[item.method.variant] && variants[item.method.variant].png) || '',
    identifier: item.method.label || '',
    amount: item.amount,
    status: item.status,
    date: item.date,
  }));

export const getWithdrawalsList = async (cursor?: string): Promise<{ cursor?: string; withdrawals: Withdrawal[] }> => {
  const res = await sendCommand<WithdrawalResponse>(paymentsWithdrawalsList, { cursor });
  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  return { cursor: res.data.cursor, withdrawals: sanitizeWithdrawals(res.data.items, res.data.variants) };
};

export const requestWithdrawal = async (payload: RequestWithdrawalPayload): Promise<PaymentData> => {
  const res = await sendCommand<WithdrawalRequestResponse>(paymentsWithdrawalsWithdraw, payload);

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }

  return res.data;
};

export const cancelWithdrawal = async (id: string): Promise<void> => {
  const res = await sendCommand<CancelWithdrawalResponse>(paymentsWithdrawalsCancel, { id });

  if (res && res.error && res.error.notification && res.error.notification.userFriendlyMessage) {
    throw new Error(res.error.notification.userFriendlyMessage);
  }
};
